import { useState, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Trash2 } from "lucide-react";
import type { Channel, ContentInput, ContentItem, ContentStatus } from "@/api/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CONTENT_STATUSES, CONTENT_FORMATS, fmtDateInput, parseDateInput } from "@/lib/content";
import { PILLARS } from "@/lib/pmbok";
import { cn } from "@/lib/utils";

/**
 * Full content editor — clicking a planner item opens this. Surfaces every
 * field the schema already stores (hook, body/draft, CTA, URL, notes) that the
 * inline row never exposed, so the board is a real drafting tool, not a list.
 * Saves the whole form through the existing update mutation; nothing new on the
 * backend. Delete reuses the existing delete mutation.
 */
export function ContentEditor({
  item,
  channels,
  canEdit,
  onClose,
  onUpdate,
  onDelete,
}: {
  item: ContentItem;
  channels: Channel[];
  canEdit: boolean;
  onClose: () => void;
  onUpdate: (id: number, patch: ContentInput) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
}) {
  const [form, setForm] = useState(() => ({
    title: item.title,
    status: item.status,
    channel_id: item.channel_id,
    pillar: item.pillar,
    format: item.format,
    piece: item.piece ?? "",
    provenance: item.provenance,
    scheduled_for: item.scheduled_for,
    hook: item.hook ?? "",
    body: item.body ?? "",
    cta: item.cta ?? "",
    url: item.url ?? "",
    notes: item.notes ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    if (!form.title.trim()) {
      setError("Give it a title.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onUpdate(item.id, {
        title: form.title.trim(),
        status: form.status,
        channel_id: form.channel_id,
        pillar: form.pillar,
        format: form.format,
        piece: form.piece.trim() || null,
        provenance: form.provenance,
        scheduled_for: form.scheduled_for,
        hook: form.hook.trim() || null,
        body: form.body.trim() || null,
        cta: form.cta.trim() || null,
        url: form.url.trim() || null,
        notes: form.notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  const ro = !canEdit;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit content</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Title">
            <Input value={form.title} disabled={ro} onChange={(e) => set("title", e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={form.status} disabled={ro} onChange={(v) => set("status", v as ContentStatus)}
                options={CONTENT_STATUSES.map((s) => ({ value: s.slug, label: s.label }))} />
            </Field>
            <Field label="Integration">
              <Select value={form.channel_id == null ? "" : String(form.channel_id)} disabled={ro}
                onChange={(v) => set("channel_id", v === "" ? null : Number(v))}
                options={[{ value: "", label: "No integration" }, ...channels.map((c) => ({ value: String(c.id), label: c.name }))]} />
            </Field>
            <Field label="Pillar">
              <Select value={form.pillar ?? ""} disabled={ro}
                onChange={(v) => set("pillar", (v === "" ? null : v) as ContentItem["pillar"])}
                options={[{ value: "", label: "No pillar" }, ...PILLARS.map((p) => ({ value: p.slug, label: p.label }))]} />
            </Field>
            <Field label="Format">
              <Select value={form.format} disabled={ro}
                onChange={(v) => set("format", v as ContentItem["format"])}
                options={CONTENT_FORMATS.map((f) => ({ value: f.slug, label: f.label }))} />
            </Field>
            <Field label="Piece">
              <Input value={form.piece} disabled={ro} onChange={(e) => set("piece", e.target.value)}
                placeholder="Source essay or article" />
            </Field>
            <Field label="Provenance">
              <Select value={form.provenance} disabled={ro}
                onChange={(v) => set("provenance", v as ContentItem["provenance"])}
                options={[{ value: "original", label: "Original" }, { value: "repost", label: "Repost" }]} />
            </Field>
          </div>

          <Field label="Scheduled for">
            <Input type="date" value={fmtDateInput(form.scheduled_for)} disabled={ro}
              onChange={(e) => set("scheduled_for", parseDateInput(e.target.value))} />
          </Field>

          <Field label="Hook" hint="The first 3 seconds — open a curiosity loop">
            <Textarea
              value={form.hook}
              disabled={ro}
              rows={2}
              onChange={(e) => set("hook", e.target.value)}
              placeholder="The line that stops the scroll…"
            />
          </Field>
          <HookCoach />

          <Field label="Draft / caption" hint="Lead with the caption hook; cut any boring stretch">
            <Textarea
              value={form.body}
              disabled={ro}
              rows={6}
              onChange={(e) => set("body", e.target.value)}
              placeholder="Write the post / script. Pick a format payoff — learn · story · win-or-lose · laugh-cry-cringe · wait-for-it…"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Call to action" hint="One clear ask">
              <Input
                value={form.cta}
                disabled={ro}
                onChange={(e) => set("cta", e.target.value)}
                placeholder="Follow · comment · grab the lead magnet…"
              />
            </Field>
            <Field label="Live URL" hint="After it's published">
              <Input value={form.url} disabled={ro} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
            </Field>
          </div>

          <Field label="Notes" hint="Format · feedback · spinoffs">
            <Textarea
              value={form.notes}
              disabled={ro}
              rows={2}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="What format is this? Who gave feedback before posting? Next spinoff?"
            />
          </Field>

          {error ? <div className="text-sm text-destructive">{error}</div> : null}
        </div>

        <div className="mt-5 flex items-center justify-between">
          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (confirm(`Delete "${item.title}"?`)) void onDelete(item.id).then(onClose);
              }}
            >
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {canEdit ? "Cancel" : "Close"}
            </Button>
            {canEdit ? (
              <Button size="sm" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** In-context hook coaching — the OnePeak "5 hook elements" + 5-second rule. */
function HookCoach() {
  return (
    <div className="-mt-1 rounded-md border border-dashed bg-muted/30 p-2.5 text-xs text-muted-foreground">
      <div className="font-semibold text-foreground">Stack the 5 hook elements ✦</div>
      <ul className="mt-1 grid gap-0.5 sm:grid-cols-2">
        <li>
          <b className="text-foreground">Verbal</b> — what you say to camera
        </li>
        <li>
          <b className="text-foreground">Text</b> — on-screen, if they miss the audio
        </li>
        <li>
          <b className="text-foreground">Visual</b> — what's shown (a point, a pattern-break)
        </li>
        <li>
          <b className="text-foreground">Audio</b> — a sizzle or beat (lowest priority)
        </li>
        <li className="sm:col-span-2">
          <b className="text-foreground">Caption</b> — first line re-hooks scrollers (the one everyone forgets)
        </li>
      </ul>
      <div className="mt-1.5 border-t pt-1.5">
        <b className="text-foreground">5-second rule:</b> if their phone died at 0:05, would they lose sleep
        wondering what happens next?
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
        {hint ? <span className="text-[10px] text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        disabled && "pointer-events-none opacity-70",
      )}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        "w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70",
        className,
      )}
      {...props}
    />
  );
}
